import { api, CHAT_TAG } from '@/api';
import { addChatDocumentDownload } from '@/store';
import { handleQueryLifecycle } from '../utils';

export interface GetChatDocumentGetPreviewRequest {
  chatDocumentId: string;
  /** Fetch the uploaded file itself, for formats the FE renders but the BE has no preview rendition of. */
  original?: boolean;
}

/** Signed Azure URL pair for in-app preview (PDF, PNG, JPEG, etc.). */
export interface GetChatDocumentGetPreviewResponse {
  downloadPath: string;
  downloadToken: string;
  /**
   * Image understanding labels. Assumed shape — staging doesn't return them yet. Read through `parseImageLabelling`.
   */
  caption?: string;
  ocrText?: string;
  /**
   * Signed URL pair for an audio file's transcript JSON. Assumed shape — staging doesn't return it yet. Read through
   * `parseAudioTranscript`.
   */
  transcriptDownloadPath?: string | null;
  transcriptDownloadToken?: string | null;
}

const chatDocumentGetPreviewApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChatDocumentGetPreview: builder.query<GetChatDocumentGetPreviewResponse, GetChatDocumentGetPreviewRequest>({
      query: (arg) => ({
        url: arg.original ? '/document/get-download' : '/document/get-preview',
        method: 'GET',
        params: { fletchDocumentId: arg.chatDocumentId },
      }),
      // `original: false` shares the cache entry of callers that leave it out.
      serializeQueryArgs: ({ queryArgs }) => ({
        chatDocumentId: queryArgs.chatDocumentId,
        original: queryArgs.original || undefined,
      }),
      providesTags: (_result, _error, arg) => [{ type: CHAT_TAG, id: arg.chatDocumentId }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await handleQueryLifecycle(queryFulfilled, (data) => {
          const { chatDocumentId } = arg;
          dispatch(addChatDocumentDownload({ ...data, chatDocumentId }));
        });
      },
    }),
  }),
});

export const { useGetChatDocumentGetPreviewQuery, useLazyGetChatDocumentGetPreviewQuery } = chatDocumentGetPreviewApi;
